import {WellnessMediaList, QueryWellnessSearchArgs} from "@irati/types"
import tracking from "@irati-tracking/wellness"
import {createUseQuery} from "@irati/utils/services/apollo"
import {gql, isNetworkRequestSettled} from "@irati/utils/gql"
import useDebounce from "@irati/utils/hooks/useDebounce"
import {useEffect, useMemo} from "react"

const useWellnessSearchBase = createUseQuery<WellnessMediaList, QueryWellnessSearchArgs>(
  gql`
    query WellnessSearch($offset: Int, $limit: Int, $search: String, $tags: [String!], $categoryId: ID) {
      wellnessSearch(offset: $offset, limit: $limit, search: $search, tags: $tags, categoryId: $categoryId) {
        count
        total
        offset
        hasMore
        results {
          id
          uuid
          title
          description
          duration
          type
          thumbnail
          category {
            id
            uuid
            name
            mediaLabel
            svgData
          }
          author {
            uuid
            name
          }
        }
      }
    }
  `,
  {key: "wellnessSearch"}
)

const DEBOUNCE_TIME = 300

export const MIN_SEARCH_LENGTH = 2

export function isSearchTermValid(searchTerm: string, minLength = MIN_SEARCH_LENGTH) {
  return typeof searchTerm === "string" && searchTerm.length >= minLength
}

export const useWellnessSearch = (
  args: QueryWellnessSearchArgs,
  opts: {
    debounceTime?: number
    minSearchLength?: number
  } = {}
) => {
  const searchInput = useMemo(() => {
    const search = args.search?.trim() ?? ""
    return {
      ...args,
      search: isSearchTermValid(search, opts.minSearchLength) ? search : ""
    }
  }, [args, opts.minSearchLength])
  const debouncedSearch = useDebounce(searchInput, opts.debounceTime ?? DEBOUNCE_TIME)
  const apollo = useWellnessSearchBase(debouncedSearch, {skip: !debouncedSearch.search})

  useEffect(() => {
    if (isNetworkRequestSettled(apollo.networkStatus) && apollo.data) {
      tracking.searchResults({
        searchTerm: apollo.variables?.search ?? "",
        resultLength: apollo.data.total ?? 0
      })
    }
  }, [apollo])

  return apollo
}
